import React, { useEffect, useState } from "react";
import { getLabelDrawerKey, getLabelDrawerOpen, setLabelDrawerOpen } from "./drawerState.js";
import ImagePanel from "./ImagePanel.js";
import { ScalePositionPanel } from "./ScalePositionPanel.js";
import { StaticTextLabel } from "./StaticTextLabel.js";
import { ImageTextPanel } from "./ImageTextPanel.js";
import {
  resolveChildTextLabels,
  getStaticImageLabelVisibilityStatus,
  getStaticImageLabelVisibilityIcon,
  getStaticImageLabelVisibilityTitle,
} from "./StaticImageLabelVisibility.js";

function getAllLabelsForStaticImageVisibility() {
  if (typeof Inventum === "undefined" || !Inventum.labels || typeof Inventum.labels.getLabels !== "function") {
    return [];
  }

  return Inventum.labels.getLabels();
}

function StaticImageLabelHeader(props) {
  const allLabels = getAllLabelsForStaticImageVisibility();
  const childTextLabels = props.childTextLabels || [];
  const resolvedChildTextLabels = resolveChildTextLabels(childTextLabels, allLabels);
  const visibilityStatus = getStaticImageLabelVisibilityStatus({
    visible: props.visible,
    childTextLabels,
    allLabels,
  });
  const visibilityIcon = getStaticImageLabelVisibilityIcon(visibilityStatus);
  const visibilityTitle = getStaticImageLabelVisibilityTitle(visibilityStatus);
  const handleClone = (e) => {
    e.stopPropagation();
    Inventum.labels.duplicate(props.id);
    props.getLabels();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    Inventum.labels.delete(props.id);
    props.getLabels();
  };

  const handleVisible = (e) => {
    e.stopPropagation();

    if (visibilityStatus === "mixed") {
      const visibleTextLabelCount = resolvedChildTextLabels.filter((label) => label.visible !== false).length;
      const hiddenTextLabelCount = resolvedChildTextLabels.length - visibleTextLabelCount;
      const showAll = visibleTextLabelCount >= hiddenTextLabelCount;

      if (showAll) {
        resolvedChildTextLabels.forEach((childLabel) => {
          if (childLabel.visible === false) {
            Inventum.labels.toggleVisible(childLabel.id);
          }
        });
      } else {
        Inventum.labels.toggleVisible(props.id);
      }
    } else {
      Inventum.labels.toggleVisible(props.id);
    }

    props.getLabels();
  };

  const handleToggleVisibleStart = (e) => {
    e.stopPropagation();
    Inventum.labels.toggleVisibleStart(props.id);
    props.getLabels();
  };

  const handleToggleTextLabels = (e) => {
    e.stopPropagation();

    if (childTextLabels.length === 0) {
      Inventum.labels.addTextToFigureImage(props.id, "below");
      setTimeout(() => {
        props.getLabels();
      }, 50);
    } else {
      props.getLabels();
    }
  };

  const handleNameChange = (e) => {
    props.setLabelName(e.target.value);
  };

  const handleNameBlur = () => {
    props.setIsEditing(false);
  };

  const handleClickEnter = (e) => {
    if (e.key === "Enter") {
      props.setIsEditing(false);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    props.setIsEditing(true);
  };

  return (
    <div onClick={props.toggleOpen} className="LabelHeader">
      <span className="PanelOpenStatus">{props.isOpen ? "▾" : "▸"}</span>
      <span className="LabelHeaderBtn delete" onClick={handleDelete} title="Delete Label">
        <i className="material-icons notranslate">clear</i>
      </span>
      <span className="LabelHeaderBtn" onClick={handleClone} title="Clone Label">
        <i className="material-icons notranslate">copy_all</i>
      </span>
      <span
        className={"LabelHeaderBtn" + (props.visibleStart ? " active" : "")}
        style={{ opacity: props.visibleStart ? 1.0 : 0.4 }}
        onClick={handleToggleVisibleStart}
        title={
          props.visibleStart
            ? "Label will appear when Scene Loads"
            : "Label won't appear when Scene Loads"
        }>
        <i className="material-icons notranslate">{props.visibleStart ? "tour" : "not_interested"}</i>
      </span>
      <span
        className={"LabelHeaderBtn" + (visibilityStatus !== "none" ? " active" : "")}
        onClick={handleVisible}
        title={visibilityTitle}
        aria-label={visibilityTitle}
        role="button">
        <i className="material-icons notranslate">{visibilityIcon}</i>
      </span>
      <span
        className={"LabelHeaderBtn" + (childTextLabels.length > 0 ? " active" : "")}
        onClick={handleToggleTextLabels}
        title={
          childTextLabels.length > 0
            ? `${childTextLabels.length} Image Text Labels - Manage in Image Text Panel`
            : "Add First Text Label"
        }>
        <i className="material-icons notranslate">text_fields</i>
        {childTextLabels.length > 0 && <span style={{ fontSize: "10px", marginLeft: "2px" }}>{childTextLabels.length}</span>}
      </span>
      <span className="LabelHeaderBtn" onClick={handleEditClick} title="Change Label Name">
        <i className="material-icons notranslate">edit</i>
      </span>
      {props.isEditing ? (
        <input
          className="StaticImageLabelNameInput"
          type="text"
          value={props.labelName}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onKeyDown={handleClickEnter}
          autoFocus
        />
      ) : (
        <span className="StaticImageLabelName" title={props.labelName}>{props.labelName}</span>
      )}
    </div>
  );
}

function StaticImageLabelDrawer(props) {
  const onImageLoad = (url) => {
    Inventum.labels.setStaticImage(props.id, url, () => {
      props.getLabels();
    });
  };

  return (
    <div className="LabelDrawerContent">
      <ImagePanel url={props.imageURL} onImageLoad={onImageLoad} {...props} />
      <ScalePositionPanel {...props} />
      <ImageTextPanel {...props} />
    </div>
  );
}

function StaticImageLabel(props) {
  const initialLabelName = Inventum.labels.getLabelName(props.id) || "Default Label Name";
  const labelDrawerKey = props.labelDrawerKey || getLabelDrawerKey(props);
  const [open, setOpen] = useState(() => getLabelDrawerOpen(labelDrawerKey));
  const [labelName, setLabelName] = useState(initialLabelName);
  const [isEditing, setIsEditing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const toggleOpen = () => {
    setOpen((currentOpen) => {
      const nextOpen = !currentOpen;
      setLabelDrawerOpen(labelDrawerKey, nextOpen);
      return nextOpen;
    });
  };

  const handleSetLabelName = (name) => {
    setLabelName(name);
    Inventum.labels.updateLabelName(props.id, name);
  };

  const handleGetLabels = () => {
    props.getLabels();
    setRefreshTrigger((prev) => prev + 1);
  };

  const childTextLabels = props.childTextLabels || [];

  return (
    <div className="LabelItem">
      <StaticImageLabelHeader
        {...props}
        toggleOpen={toggleOpen}
        labelName={labelName}
        isEditing={isEditing}
        setLabelName={handleSetLabelName}
        setIsEditing={setIsEditing}
        isOpen={open}
        childTextLabels={childTextLabels}
        getLabels={handleGetLabels}
      />
      {open ? (
        <div className="LabelItemDrawer">
          <StaticImageLabelDrawer {...props} childTextLabels={childTextLabels} getLabels={handleGetLabels} />
        </div>
      ) : null}
    </div>
  );
}

export { StaticImageLabel };
